"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Plus, Trash2 } from "lucide-react";
import { FormInput } from "../form-input";
import { RequiredMark } from "../required-mark";
import { cn } from "../utils";
import { ErrorMessage } from "./ErrorMessage";
export type RepeaterColumnType = "text" | "email" | "phone" | "number";
export interface RepeaterColumn {
  /** Heading shown above the column. */
  label: string;
  /** Key used for this column's value within each row object. */
  columnName: string;
  /** Input type rendered for this column. */
  type?: RepeaterColumnType | null;
  /** Whether a value is required in every row. */
  required?: boolean | null;
}
export type RepeaterRow = Record<string, string>;
export interface InputRepeaterProps {
  /** Field name — used for the legend `id` and input naming. */
  name: string;
  /** Label rendered above the list. */
  label?: string | null;
  /** Long-form description rendered above the list — plain string or rich content (e.g. a description with a link). */
  description?: React.ReactNode;
  /** Columns each row exposes. */
  columns: RepeaterColumn[];
  /** Current rows. Each row is an object keyed by `columnName`. */
  value?: RepeaterRow[];
  /** Emits the full row set whenever any cell changes or a row is added/removed. */
  onChange?: (rows: RepeaterRow[]) => void;
  /** Blur handler for form integration. */
  onBlur?: () => void;
  /** Whether at least one row is required. */
  required?: boolean;
  /** Server / validation error rendered below the list. */
  error?: string;
  /** Text on the add-row button. Defaults to "Add row". */
  addButtonLabel?: string | null;
  /** Cap on the number of rows. */
  maxRows?: number | null;
  /** Disables interaction. */
  disabled?: boolean;
}
const inputTypeFor = (type?: RepeaterColumnType | null): React.HTMLInputTypeAttribute => {
  switch (type) {
    case "email":
      return "email";
    case "phone":
      return "tel";
    case "number":
      return "number";
    default:
      return "text";
  }
};
const emptyRow = (columns: RepeaterColumn[]): RepeaterRow => {
  const row: RepeaterRow = {};
  for (const column of columns) {
    if (column.columnName) row[column.columnName] = "";
  }
  return row;
};

/**
 * A repeatable table of inputs — visitors add and remove rows, each row holding
 * one value per configured column. Mirrors a Gravity Forms "List" field. The
 * value is an array of row objects keyed by `columnName`; the parent form
 * serialises it as JSON when building FormData.
 */
export const InputRepeater = t0 => {
  const $ = _c(49);
  const {
    name,
    label,
    description,
    columns,
    value,
    onChange,
    onBlur,
    required,
    error,
    addButtonLabel,
    maxRows,
    disabled
  } = t0;
  let atMax;
  let handleAddRow;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== columns || $[1] !== description || $[2] !== disabled || $[3] !== error || $[4] !== label || $[5] !== maxRows || $[6] !== name || $[7] !== onBlur || $[8] !== onChange || $[9] !== required || $[10] !== value) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const validColumns = columns.filter(_temp);
      const rows = Array.isArray(value) && value.length > 0 ? value : [];
      const descId = description ? `${name}-desc` : undefined;
      atMax = typeof maxRows === "number" && rows.length >= maxRows;
      let t9;
      if ($[21] !== onChange) {
        t9 = next => {
          onChange?.(next);
        };
        $[21] = onChange;
        $[22] = t9;
      } else {
        t9 = $[22];
      }
      const updateRows = t9;
      const handleCellChange = (rowIndex, columnName, cellValue) => {
        const next_0 = rows.map((row, index) => index === rowIndex ? {
          ...row,
          [columnName]: cellValue
        } : row);
        updateRows(next_0);
      };
      handleAddRow = () => {
        if (atMax) {
          return;
        }
        updateRows([...rows, emptyRow(validColumns)]);
      };
      const handleRemoveRow = rowIndex_0 => {
        updateRows(rows.filter((_, index_0) => index_0 !== rowIndex_0));
        onBlur?.();
      };
      if (validColumns.length === 0) {
        t8 = null;
        break bb0;
      }
      t3 = "flex w-full flex-col gap-3";
      t4 = descId;
      t5 = !!error || undefined;
      if ($[23] !== label || $[24] !== required) {
        t6 = Boolean(label) && <legend className="typography-component-sm-regular desktop:typography-component-md-regular text-input-text-label">{label}<RequiredMark required={required} /></legend>;
        $[23] = label;
        $[24] = required;
        $[25] = t6;
      } else {
        t6 = $[25];
      }
      if ($[26] !== descId || $[27] !== description) {
        t7 = description && <p id={descId} className="typography-component-sm-regular desktop:typography-component-md-regular text-input-text-label">{description}</p>;
        $[26] = descId;
        $[27] = description;
        $[28] = t7;
      } else {
        t7 = $[28];
      }
      t1 = "mt-2 flex flex-col gap-6";
      t2 = rows.map((row_0, rowIndex_1) => <div key={rowIndex_1} className={cn("flex flex-col gap-3", rowIndex_1 > 0 && "border-t border-border-default pt-6")}><p className="typography-component-sm-medium text-input-text-label">{rowIndex_1 + 1}.</p><div className="grid gap-3 tablet:grid-cols-[1fr_auto] tablet:items-end"><div className="grid gap-3 tablet:grid-cols-2">{validColumns.map(column_0 => <FormInput key={column_0.columnName} id={`${name}-${rowIndex_1}-${column_0.columnName}`} name={`${name}-${rowIndex_1}-${column_0.columnName}`} type={inputTypeFor(column_0.type)} inputMode={column_0.type === "number" ? "numeric" : undefined} label={column_0.label} required={column_0.required ?? undefined} value={row_0[column_0.columnName] ?? ""} onChange={event => handleCellChange(rowIndex_1, column_0.columnName, event.target.value)} onBlur={onBlur} disabled={disabled} />)}</div><button type="button" onClick={() => handleRemoveRow(rowIndex_1)} disabled={disabled} className={cn("inline-flex items-center justify-center gap-1.5 rounded-lg border border-input-border px-3 py-2.5", "typography-component-sm-regular desktop:typography-component-md-regular text-input-text-label", "hover:border-input-border-hover hover:text-text-feedback-error", "focus:outline-none focus:ring-1 focus:ring-input-border-focus", "disabled:opacity-50")} aria-label={`Remove row ${rowIndex_1 + 1}`}><Trash2 className="size-4" aria-hidden="true" /><span className="tablet:sr-only">Remove</span></button></div></div>);
    }
    $[0] = columns;
    $[1] = description;
    $[2] = disabled;
    $[3] = error;
    $[4] = label;
    $[5] = maxRows;
    $[6] = name;
    $[7] = onBlur;
    $[8] = onChange;
    $[9] = required;
    $[10] = value;
    $[11] = atMax;
    $[12] = handleAddRow;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    atMax = $[11];
    handleAddRow = $[12];
    t1 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[29] !== t1 || $[30] !== t2) {
    t9 = <div className={t1}>{t2}</div>;
    $[29] = t1;
    $[30] = t2;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const t10 = disabled || atMax;
  let t11;
  let t12;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("inline-flex items-center gap-1.5 rounded-lg border border-input-border px-4 py-2.5", "typography-component-xs-regular text-input-text", "hover:border-input-border-hover", "focus:outline-none focus:ring-1 focus:ring-input-border-focus", "disabled:opacity-50 disabled:cursor-not-allowed");
    t12 = <Plus className="size-4" aria-hidden="true" />;
    $[32] = t11;
    $[33] = t12;
  } else {
    t11 = $[32];
    t12 = $[33];
  }
  const t13 = addButtonLabel || "Add row";
  let t14;
  if ($[34] !== handleAddRow || $[35] !== t10 || $[36] !== t13) {
    t14 = <div><button type="button" onClick={handleAddRow} disabled={t10} className={t11}>{t12}{t13}</button></div>;
    $[34] = handleAddRow;
    $[35] = t10;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== error) {
    t15 = <ErrorMessage error={error} />;
    $[38] = error;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t14 || $[41] !== t15 || $[42] !== t3 || $[43] !== t4 || $[44] !== t5 || $[45] !== t6 || $[46] !== t7 || $[47] !== t9) {
    t16 = <fieldset className={t3} aria-describedby={t4} aria-invalid={t5}>{t6}{t7}{t9}{t14}{t15}</fieldset>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t3;
    $[43] = t4;
    $[44] = t5;
    $[45] = t6;
    $[46] = t7;
    $[47] = t9;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
};
InputRepeater.displayName = "InputRepeater";
function _temp(column) {
  return Boolean(column.columnName);
}